import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { purchaserReviewCopy } from "@/lib/manufacturing/plantMaterialRequest";
import {
  canSeePlantRequestStatusFromHasRole,
  canSeePurchaserRequestAlertFromHasRole,
} from "@/lib/manufacturing/plantMaterialRequestAccess";
import { useAuth } from "@/contexts/AuthContext";
import type { PlantRequestDockItem } from "@/hooks/useMfgDockSignals";

export function ActivityDockPlantMaterialRequestSection(props: {
  reviewCount: number | null;
  statusItems: readonly PlantRequestDockItem[] | null;
  onOpenMfgPurchasing?: () => void;
}) {
  const auth = useAuth();
  const hasRole = (...r: string[]) => auth.hasRole(...r);
  const purchaser = canSeePurchaserRequestAlertFromHasRole(hasRole);
  const plantOps = canSeePlantRequestStatusFromHasRole(hasRole);
  const reviewCount = props.reviewCount ?? 0;
  const items = props.statusItems ?? [];

  if (purchaser && reviewCount > 0) {
    const text = purchaserReviewCopy(reviewCount);
    return (
      <section
        aria-label="Material request for purchasing"
        {...{ [FIREFLY_TARGET_ATTR]: "plant-material-request-review" }}
        className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/60 dark:bg-amber-950/30"
      >
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
          Material request
        </h3>
        <p className="mt-1 text-[12px] text-amber-900 dark:text-amber-200">{text}</p>
        {props.onOpenMfgPurchasing ? (
          <button
            type="button"
            onClick={props.onOpenMfgPurchasing}
            className="mt-2 rounded-lg bg-amber-700 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-amber-800 dark:bg-amber-600"
          >
            Review in Purchasing
          </button>
        ) : null}
      </section>
    );
  }

  if (!plantOps || items.length === 0) return null;

  return (
    <section
      aria-label="Material request status"
      {...{ [FIREFLY_TARGET_ATTR]: "plant-material-request-status" }}
      className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-900"
    >
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-zinc-200">
        Material requests
      </h3>
      <ul className="mt-1 space-y-1">
        {items.map((item) => (
          <li key={item.id} className="text-[12px] text-gray-800 dark:text-zinc-100">
            <span className="font-semibold">{item.poNumber}.</span> {item.copy}
          </li>
        ))}
      </ul>
    </section>
  );
}
